import { fullViewportRect } from "@slot/presentation";
import type { DesignRect, ViewportSnapshot } from "@slot/presentation";

/**
 * The control bar's reserved bands per shape, in design units (the mobile bar prefab's group sizes:
 * the top badge strip, the left column, the right column, the bottom balance / bet / win row). The
 * bar element lays its groups out from the same numbers (the chrome root hands them over as custom
 * properties), so the grid's content area and the bar cannot drift apart.
 */
export const CHROME_BANDS = {
  top: 80,
  left: 202.6,
  right: 344.7,
  bottom: 164.5,
  /** Mobile landscape shows the portrait-authored groups at the portrait-equivalent height, capped per group. */
  landscapeMultiplierCap: 1.5,
  landscapeRightMultiplierCap: 2,
} as const;

/** The landscape group multipliers: `min(cap, max(1, width / height))`, one for the right column and one for the rest. */
export function landscapeMultipliers(snapshot: ViewportSnapshot): { m: number; mRight: number } {
  const aspect = Math.max(1, snapshot.css.width / Math.max(1, snapshot.css.height));
  return { m: Math.min(CHROME_BANDS.landscapeMultiplierCap, aspect), mRight: Math.min(CHROME_BANDS.landscapeRightMultiplierCap, aspect) };
}

/**
 * The rectangle free of chrome for the game's presentation. Mobile landscape subtracts the four bands
 * (the badge strip stays reserved so the grid never moves when a feature mode activates); desktop and
 * portrait keep the whole viewport, where the bar overlays the composition's authored bottom margin.
 */
export function resolveChromeContentArea(snapshot: ViewportSnapshot): DesignRect {
  const full = fullViewportRect(snapshot);
  if (snapshot.shape !== "mobile_landscape") return full;
  const { m, mRight } = landscapeMultipliers(snapshot);
  return {
    left: full.left + CHROME_BANDS.left * m,
    top: full.top + CHROME_BANDS.top * m,
    right: full.right - CHROME_BANDS.right * mRight,
    bottom: full.bottom - CHROME_BANDS.bottom * m,
  };
}

/** The custom properties the chrome root carries for the bar's and the header's stylesheets (unitless design units and multipliers). */
export function chromeBandProperties(snapshot: ViewportSnapshot): Record<string, string> {
  const { m, mRight } = landscapeMultipliers(snapshot);
  return {
    "--slot-band-top": String(CHROME_BANDS.top),
    "--slot-band-left": String(CHROME_BANDS.left),
    "--slot-band-right": String(CHROME_BANDS.right),
    "--slot-band-bottom": String(CHROME_BANDS.bottom),
    "--slot-landscape-m": snapshot.shape === "mobile_landscape" ? m.toFixed(4) : "1",
    "--slot-landscape-m-right": snapshot.shape === "mobile_landscape" ? mRight.toFixed(4) : "1",
  };
}
